import { NetworkProps, enabledNetworks } from '@dapp/multi-lib/network';
import { useToast } from '@dapp/multi-lib/ui/toast';
import { OptionProfile, StatefulMenu } from 'baseui/menu';
import { PLACEMENT, StatefulPopover } from 'baseui/popover';
import { LabelSmall } from 'baseui/typography';
import { cx } from 'class-variance-authority';
import { isNil } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import styles from '../bridge.module.css';
import Image from 'next/image';
import { useAtom } from 'jotai';
import { bridgeAtom } from 'structure/bridge/store/bridgeAtom';
import { getStaticAssetUrl } from 'utils/url';
import { useConnection } from 'wagmi';

interface INetworks {
  id: number;
  network: NetworkProps;
  subtitle: string;
  imgUrl: string;
}

export const BridgeNetworkSelection: React.FC<{
  allowedNetworks?: number[];
}> = ({ allowedNetworks }) => {
  const [networks, setNetworks] = useState<INetworks[]>([]);
  const [bridgeState, setBridgeState] = useAtom(bridgeAtom);
  const [selectedNetwork, setSelectedNetwork] = useState<INetworks>();
  const { chain } = useConnection();
  const { t } = useTranslation('migrate');
  const toastV2 = useToast();

  useEffect(() => {
    const supportedNetworks = enabledNetworks
      .filter((item) => {
        if (allowedNetworks && allowedNetworks.length > 0) {
          return allowedNetworks.includes(item.id);
        }
        return false;
      })
      .map((n) => {
        return {
          id: n.id,
          network: n,
          subtitle: n.name,
          imgUrl: `${process.env.NEXT_PUBLIC_STATIC_ASSETS_BUCKET}/sfi/icons/tokens/${n.shortName}.png`,
        };
      });

    setNetworks(supportedNetworks);
  }, [allowedNetworks]);

  const network = useMemo(() => {
    const selectedNetworkId = chain?.id;
    if (
      isNil(selectedNetworkId) ||
      !networks.find((obj) => obj.id === selectedNetworkId)
    ) {
      return networks[0];
    } else {
      return networks.find((n) => n.network.id === selectedNetworkId);
    }
  }, [chain?.id, networks]);

  useEffect(() => {
    if (network?.network) {
      handleNetworkChange(network.network);
    }
  }, [networks]);

  const handleNetworkChange = (chain: NetworkProps) => {
    try {
      const selectedNetworkData = {
        id: chain.id,
        network: chain,
        subtitle: chain.name,
        imgUrl: `${process.env.NEXT_PUBLIC_STATIC_ASSETS_BUCKET}/sfi/icons/tokens/${chain.shortName}.png`,
      };
      setSelectedNetwork(selectedNetworkData);
      const prevState = bridgeState;
      setBridgeState({ ...prevState, dstChainId: chain?.id });
    } catch {
      toastV2({
        title: t('common:ERROR_MESSAGES.ERROR'),
        description: t('common:NETWORK_CHANGE_ERROR'),
        type: 'error',
      });
    }
  };

  return (
    <>
      <LabelSmall
        className={cx(twMerge(styles.label, 'mb-2 text-sm text-white-600'))}
      >
        {t('NETWORK')}
      </LabelSmall>
      <StatefulPopover
        focusLock
        placement={PLACEMENT.bottom}
        content={({ close }) => (
          <StatefulMenu
            items={networks}
            onItemSelect={(n) => {
              handleNetworkChange(n.item.network);
              close();
            }}
            overrides={{
              List: {
                style: {
                  height: 'max-content',
                  backgroundColor: 'white',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  borderTopLeftRadius: '0px',
                  borderTopRightRadius: '0px',
                  borderBottomLeftRadius: '0px',
                  borderBottomRightRadius: '0px',
                  border: '1px solid #B5B5B5 !important',
                  background: '#000000',
                  color: 'var(--style-20-ffffff-light-text, #FFF)',
                },
              },
              Option: {
                component: OptionProfile,
                props: {
                  getProfileItemLabels: ({
                    subtitle,
                  }: {
                    subtitle: string;
                  }) => ({
                    subtitle,
                  }),
                  getProfileItemImg: (item: INetworks) => item.imgUrl,
                  getProfileItemImgText: (item: INetworks) => item.subtitle,
                  overrides: {
                    ListItemProfile: {
                      style: {
                        paddingTop: '0px',
                        paddingBottom: '0px',
                        paddingLeft: '0px',
                        paddingRight: '0px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'start',
                      },
                    },
                    ProfileImg: {
                      style: {
                        width: '32px',
                        height: '32px',
                      },
                    },
                    ProfileLabelsContainer: {
                      style: {
                        marginRight: '24px',
                        marginTop: '24px',
                        marginBottom: '24px',
                        marginLeft: '-8px',
                      },
                    },
                    ProfileSubtitle: {
                      style: {
                        'font-size': '14px',
                        'font-family': 'Montserrat !important',
                        'font-style': 'normal',
                        'font-weight': 500,
                        'line-height': '22px',
                      },
                    },
                  },
                },
              },
            }}
          />
        )}
      >
        <div className='w-full cursor-pointer'>
          <div
            className={
              'flex w-full items-center justify-between border-[0.25px] border-black-300 bg-box-primary p-4 text-white'
            }
          >
            <div className='flex items-center gap-2'>
              <Image
                src={selectedNetwork?.imgUrl ?? ''}
                className='rounded-full'
                alt='TokenIcon'
                width={24}
                height={24}
              />
              <LabelSmall className={cx(twMerge(styles.bridgeFont, 'text-lg'))}>
                {selectedNetwork?.network.name}
              </LabelSmall>
            </div>
            <div className='mr-1'>
              <Image
                src={getStaticAssetUrl('/sfi/static/svgs/arrow-down.svg')}
                width={16}
                height={16}
                alt='arrow-down'
              />
            </div>
          </div>
        </div>
      </StatefulPopover>
    </>
  );
};
